// EV-mode inter-lock for the output-formula-picker: selecting an EV formula
// claims its (mediaSide, conventionId) pairs; other EV rows that would share
// any of the same pairs are disabled with a tooltip pointing at the blocker.
//
// Per-media-side scoping: two EV formulas can coexist if they share a
// stills-side convention only when neither uses videos of that convention
// (mockup §"EV inter-lock — clarification questions" Q4, default assumption
// wired here).

import { OutputFormula } from 'src/app/models/output-formulas/output-formula';
import {
  outputFormulaValueCoversStills,
  outputFormulaValueCoversVideos,
} from 'src/app/models/output-formulas/output-formula-value';

export type MediaSide = 'stills' | 'videos';

export interface CoveredPair {
  media: MediaSide;
  conventionId: string;
  blockerFormulaId: string;
  blockerFormulaName: string;
  blockerConventionName: string;
}

export interface EvInterlockBlocker {
  disabledFormulaId: string;
  blockerFormulaId: string;
  blockerFormulaName: string;
  conventionName: string;
  media: MediaSide;
}

export const evConventionIdsFor = (formula: OutputFormula): readonly string[] => {
  switch (formula.value.kind) {
    case 'evStills':
    case 'evVideos':
    case 'evBoth':
      return formula.value.evConventionIds;
    default:
      return [];
  }
};

const usesMediaSide = (formula: OutputFormula, media: MediaSide): boolean =>
  media === 'stills'
    ? outputFormulaValueCoversStills(formula.value)
    : outputFormulaValueCoversVideos(formula.value);

const mediaSidesUsed = (formula: OutputFormula): readonly MediaSide[] => {
  const sides: MediaSide[] = [];
  const withStills = outputFormulaValueCoversStills(formula.value) ? [...sides, 'stills' as MediaSide] : sides;
  return outputFormulaValueCoversVideos(formula.value)
    ? [...withStills, 'videos' as MediaSide]
    : withStills;
};

// Build the set of (mediaSide, conventionId) pairs already claimed by the
// current EV selection. Each covered pair remembers the blocker formula for
// tooltip attribution.
export const coveredPairsFor = (
  selectedFormulas: readonly OutputFormula[],
  conventionNamesById: ReadonlyMap<string, string>,
): CoveredPair[] =>
  selectedFormulas.flatMap((formula) =>
    mediaSidesUsed(formula).flatMap((media) =>
      evConventionIdsFor(formula).map((conventionId) => ({
        media,
        conventionId,
        blockerFormulaId: formula.id,
        blockerFormulaName: formula.name,
        blockerConventionName: conventionNamesById.get(conventionId) ?? 'Unknown convention',
      })),
    ),
  );

// A candidate EV formula is disabled if any of its (mediaSide, conventionId)
// pairs collides with a covered pair. Returns the first collision found so
// the tooltip has a specific blocker to name; deterministic (order of
// mediaSides then order of evConventionIds).
export const computeInterlockBlocker = (
  candidate: OutputFormula,
  coveredPairs: readonly CoveredPair[],
  selectedFormulaIds: ReadonlySet<string>,
): EvInterlockBlocker | null => {
  if (selectedFormulaIds.has(candidate.id)) return null;
  const candidateConventionIds = evConventionIdsFor(candidate);
  if (candidateConventionIds.length === 0) return null;
  const mediaSides = mediaSidesUsed(candidate);
  const firstCollision = mediaSides
    .flatMap((media) =>
      candidateConventionIds.flatMap((conventionId) =>
        coveredPairs
          .filter((pair) => pair.media === media && pair.conventionId === conventionId)
          .map((pair) => ({ media, conventionId, pair })),
      ),
    )[0];
  if (firstCollision == null) return null;
  return {
    disabledFormulaId: candidate.id,
    blockerFormulaId: firstCollision.pair.blockerFormulaId,
    blockerFormulaName: firstCollision.pair.blockerFormulaName,
    conventionName: firstCollision.pair.blockerConventionName,
    media: firstCollision.media,
  };
};

export const buildInterlockMap = (
  candidates: readonly OutputFormula[],
  selectedFormulas: readonly OutputFormula[],
  conventionNamesById: ReadonlyMap<string, string>,
): Map<string, EvInterlockBlocker> => {
  const selectedIds = new Set(selectedFormulas.map((formula) => formula.id));
  const coveredPairs = coveredPairsFor(selectedFormulas, conventionNamesById);
  return new Map(
    candidates
      .map((candidate) => computeInterlockBlocker(candidate, coveredPairs, selectedIds))
      .filter((blocker): blocker is EvInterlockBlocker => blocker != null)
      .map((blocker) => [blocker.disabledFormulaId, blocker] as const),
  );
};

export const interlockTooltip = (blocker: EvInterlockBlocker): string =>
  `"${blocker.conventionName}" already covered by ${blocker.blockerFormulaName}`;

// Exported for reachability in tests that reason about which media sides a
// candidate might collide on — keeps the collision-side calculation testable
// separately from the whole computeInterlockBlocker orchestration.
export const _usesMediaSide = usesMediaSide;
